<template>
	<AppLayout
		:title="academicCertificate.user.full_name"
		:subtitle="academicCertificate.user.id_number"
		:return-url="route('tools.academic-certificates.submissions.index')"
		:return-label="trans('tools.academic-certificates.submissions.title')"
	>
		<Modal id="accept-academic-certificate-modal">
			<template #title>{{ academicCertificate.user.full_name }}</template>
			<template #subtitle>{{ academicCertificate.user.id_number }}</template>
			<LabeledObject required>
				<template #label>{{ trans('tools.academic-certificates.expires_at') }}</template>
				<IconDateInput v-model="academicCertificate.expires_at" icon-class="fa-solid fa-clock" full/>
			</LabeledObject>
			<template #footer>
				<PrimaryButton :color="Color.Green" @click.stop="accept">
					<i class="fa-solid fa-check mr-2"/>
					{{ trans('general.accept') }}
				</PrimaryButton>
			</template>
		</Modal>
		<Modal id="reject-academic-certificate-modal">
			<template #title>{{ academicCertificate.user.full_name }}</template>
			<template #subtitle>{{ academicCertificate.user.id_number }}</template>
			<LabeledObject required>
				<template #label>{{ trans('tools.academic-certificates.rejection_note') }}</template>
				<textarea v-model="academicCertificate.rejection_note"/>
			</LabeledObject>
			<template #footer>
				<PrimaryButton :color="Color.Red" @click.stop="reject">
					<i class="fa-solid fa-ban mr-2"/>
					{{ trans('general.reject') }}
				</PrimaryButton>
			</template>
		</Modal>
		<template #actions>
			<SecondaryButton :color="Color.Red" @click.stop="showModal('reject-academic-certificate-modal')">
				<i class="fa-solid fa-ban mr-2"/>
				{{ trans('general.reject') }}
			</SecondaryButton>
			<PrimaryButton :color="Color.Green" @click.stop="showModal('accept-academic-certificate-modal')">
				<i class="fa-solid fa-check mr-2"/>
				{{ trans('general.accept') }}
			</PrimaryButton>
		</template>
		<iframe :src="route('tools.academic-certificates.submissions.show', academicCertificate.id)" class="w-full rounded-lg border border-gray-200 dark:border-gray-800 h-[1000px]"/>
	</AppLayout>
</template>
<script setup lang="ts">
import axios from 'axios'
import {route} from 'ziggy-js'
import {router} from '@inertiajs/vue3'
import {trans} from 'laravel-vue-i18n'
import {Color} from '@/Types/Enums/Color'
import {defineProps, ref, type Ref} from 'vue'
import AppLayout from '@/Layouts/AppLayout.vue'
import Modal from '@/Components/UI/Modals/Modal.vue'
import LabeledObject from '@/Components/UI/LabeledObject.vue'
import IconDateInput from '@/Components/UI/Inputs/IconDateInput.vue'
import PrimaryButton from '@/Components/UI/Buttons/PrimaryButton.vue'
import SecondaryButton from '@/Components/UI/Buttons/SecondaryButton.vue'
import {type AcademicCertificate} from '@/Types/Tools/AcademicCertificate'
import {showInfo, showModal, showSuccess, showValidationErrors} from '@/Helpers/general'

const props = defineProps<{
	academicCertificate:AcademicCertificate,
}>()

const working:Ref<boolean> = ref(false)
const academicCertificate:Ref<AcademicCertificate> = ref(props.academicCertificate)

function accept():void{
	if(!working.value){
		working.value = true
		showInfo(trans('general.updating'))
		axios.patch(route('tools.academic-certificates.submissions.update', academicCertificate.value.id), {
			action: 'accept',
			expires_at: academicCertificate.value.expires_at,
		}).then(() => {
			showSuccess(trans('general.updated'))
			router.get(route('tools.academic-certificates.submissions.index'))
		}).catch(showValidationErrors).finally(() => {
			working.value = false
		})
	}
}

function reject():void{
	if(!working.value){
		working.value = true
		showInfo(trans('general.updating'))
		axios.patch(route('tools.academic-certificates.submissions.update', academicCertificate.value.id), {
			action: 'reject',
			rejection_note: academicCertificate.value.rejection_note,
		}).then(() => {
			showSuccess(trans('general.updated'))
			router.get(route('tools.academic-certificates.submissions.index'))
		}).catch(showValidationErrors).finally(() => {
			working.value = false
		})
	}
}
</script>